/**
 * Auth Store
 * Zustand-based authentication state for phone OTP auth
 */

import { USER_TYPES, type UserType } from "@shared/types";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { useNotificationsStore } from "@/features/settings/store/notifications.store";
import { useStudioStore } from "@/features/studio/shell/studio-context";
import { observeRoleResponses, observeSessionLoss } from "@/lib/api-client";
import { queryClient } from "@/lib/query-client";
import { analytics } from "@/services/analytics.service";
import type { FinishdUser } from "../services/auth.service";
import * as authService from "../services/auth.service";
import { showRoleSwitchedElsewhereNotice } from "../utils/role-switch-notice";
import { useRoleNotActiveStore } from "./role-not-active.store";

interface AuthState {
	// State
	user: FinishdUser | null;
	isLoading: boolean;
	error: string | null;
	otpSent: boolean;
	pendingPhone: string | null;

	// Actions
	sendOtp: (phone: string) => Promise<void>;
	verifyOtp: (phone: string, otp: string) => Promise<void>;
	googleSignIn: (
		idToken: string,
	) => Promise<{ linkRequired: boolean; email?: string }>;
	logout: () => Promise<void>;
	setUserType: (userType: UserType) => Promise<void>;
	/**
	 * Make `role` the active role. Resolves to the new user so the caller can
	 * navigate; role data is cleared only once the server has accepted.
	 */
	switchRole: (role: UserType) => Promise<FinishdUser>;
	/** Add `role` to the account and make it active, as `switchRole` does. */
	addRole: (role: UserType) => Promise<FinishdUser>;
	setLanguagePreference: (languagePreference: "en" | "hi") => Promise<void>;
	setUser: (user: FinishdUser | null) => void;
	setPhone: (phone: string) => void;
	markProfileComplete: () => void;
	refreshUser: () => Promise<void>;
	clearError: () => void;
	resetOtpState: () => void;

	// Computed helpers
	isAuthenticated: () => boolean;
	needsOnboarding: () => boolean;
	needsProfileSetup: () => boolean;
	needsPhoneCollection: () => boolean;
	getUserType: () => UserType | null;
}

// ============================================================================
// PERSIST MIGRATION
// ============================================================================

/**
 * Validate/repair the persisted auth state on rehydrate. Exported so it can be
 * tested directly (the persist middleware is mocked out in store tests).
 *
 * phone may legitimately be null for Google accounts (lazy phone collection),
 * so only `id` is required — making phone required again here would silently log
 * out every Google user on the next deploy.
 */
export function migrateAuthState(
	persisted: unknown,
	version: number,
): { user: FinishdUser | null } {
	if (version === 0 || typeof persisted !== "object" || persisted === null) {
		return { user: null };
	}
	const state = persisted as Record<string, unknown>;
	if (state.user && typeof state.user === "object") {
		const user = state.user as Record<string, unknown>;
		if (
			typeof user.id !== "string" ||
			(user.phone !== null && typeof user.phone !== "string")
		) {
			return { user: null };
		}
	}
	return state as { user: FinishdUser | null };
}

// ============================================================================
// ACCOUNT CHANGE
// ============================================================================

const AUTH_STORE_KEY = "finishd-auth-store";

function isUserType(value: string): value is UserType {
	return (USER_TYPES as readonly string[]).includes(value);
}

/**
 * The same account, with a different active role. A first pick of a role
 * (from no role) is not a switch: there is no previous role's data to clear.
 */
function isRoleSwitch(
	previous: FinishdUser | null,
	next: FinishdUser | null,
): next is FinishdUser & { userType: UserType } {
	return (
		!!previous &&
		!!next &&
		previous.id === next.id &&
		previous.userType !== null &&
		next.userType !== null &&
		previous.userType !== next.userType
	);
}

/**
 * Drop everything held for the active role: every query (keys are not scoped
 * by role), the notifications list, the studio's project state and a pending
 * ROLE_NOT_ACTIVE switch prompt.
 */
export function resetRoleScopedState(): void {
	void queryClient.cancelQueries();
	queryClient.clear();
	useNotificationsStore.getState().reset();
	useStudioStore.getState().reset();
	useRoleNotActiveStore.getState().clear();
}

/**
 * Drop every cached query when the signed-in account changes, and all role
 * data (`resetRoleScopedState`) when the same account's active role changes
 * from one role to another. Query keys such
 * as `settings.homeownerProfile()` are not scoped by user, so without this a
 * sign-in that does not follow `logout()` -- or a `refreshUser` that comes back
 * as the account another tab has since signed in to -- shows the previous
 * account's cached name, avatar and data.
 */
function clearCacheOnAccountChange(
	previous: FinishdUser | null,
	next: FinishdUser | null,
): void {
	if (previous?.id !== next?.id) {
		queryClient.clear();
		useRoleNotActiveStore.getState().clear();
	} else if (isRoleSwitch(previous, next)) {
		resetRoleScopedState();
	}
}

/**
 * How many of this tab's own `switchRole` / `addRole` calls are awaiting the
 * server. While any is, `handleActiveRoleHeader` ignores the header, which
 * may already name the role this tab is switching to. A call that fails
 * then reconciles through `reconcileActiveRole`, since a header ignored
 * meanwhile may have named a role set on another device. When a refresh is
 * already out it joins that one, whose answer may predate a later switch;
 * the next response's header starts another refresh in that case.
 */
let ownRoleChangesInFlight = 0;

// ============================================================================
// STORE
// ============================================================================

export const useAuthStore = create<AuthState>()(
	persist(
		(set, get) => ({
			// Initial state
			user: null,
			isLoading: false,
			error: null,
			otpSent: false,
			pendingPhone: null,

			/**
			 * Send OTP to phone number
			 */
			sendOtp: async (phone: string) => {
				set({ isLoading: true, error: null, otpSent: false });
				try {
					await authService.sendOtp(phone);
					analytics.auth.otpSent();
					set({ otpSent: true, pendingPhone: phone, isLoading: false });
				} catch (error) {
					const err = error as { response?: { data?: { message?: string } } };
					const errorMessage =
						err.response?.data?.message || "Failed to send OTP";
					set({ error: errorMessage, isLoading: false });
					throw error;
				}
			},

			/**
			 * Verify OTP and authenticate
			 */
			verifyOtp: async (phone: string, otp: string) => {
				set({ isLoading: true, error: null });
				try {
					const response = await authService.verifyOtp(phone, otp);
					const user = response.data.user;
					// Bind subsequent events to this user. `newUser` is true when
					// the backend hasn't yet persisted a userType (onboarding due).
					analytics.identify(user.id, {
						userType: user.userType ?? undefined,
						createdAt: user.createdAt,
					});
					analytics.auth.otpVerified({ newUser: user.isNewUser });
					clearCacheOnAccountChange(get().user, user);
					set({
						user,
						isLoading: false,
						otpSent: false,
						pendingPhone: null,
					});
				} catch (error) {
					const err = error as { response?: { data?: { message?: string } } };
					const errorMessage = err.response?.data?.message || "Invalid OTP";
					analytics.auth.loginFailed({
						reason: errorMessage,
					});
					set({ error: errorMessage, isLoading: false });
					throw error;
				}
			},

			/**
			 * Sign in with a Google ID token. Resolves to { linkRequired } so the
			 * UI can prompt the user to connect Google to an existing phone account
			 * instead of logging in.
			 */
			googleSignIn: async (idToken: string) => {
				set({ isLoading: true, error: null });
				try {
					const response = await authService.signInWithGoogle(idToken);
					const data = response.data;
					if (response.success && "user" in data) {
						const { user: signedInUser, isNewUser } = data;
						const user = { ...signedInUser, isNewUser };
						analytics.identify(user.id, {
							userType: user.userType ?? undefined,
							createdAt: user.createdAt,
						});
						analytics.auth.otpVerified({ newUser: isNewUser });
						clearCacheOnAccountChange(get().user, user);
						set({
							user,
							isLoading: false,
							otpSent: false,
							pendingPhone: null,
						});
						return { linkRequired: false };
					}
					// Email already belongs to a phone account — linking required.
					set({ isLoading: false });
					return {
						linkRequired: true,
						email: "email" in data ? data.email : undefined,
					};
				} catch (error) {
					const err = error as { response?: { data?: { message?: string } } };
					const errorMessage =
						err.response?.data?.message || "Google sign-in failed";
					analytics.auth.loginFailed({ reason: errorMessage });
					set({ error: errorMessage, isLoading: false });
					throw error;
				}
			},

			/**
			 * Logout user
			 */
			logout: async () => {
				try {
					await authService.logout();
				} finally {
					analytics.auth.logout();
					analytics.reset();
					queryClient.clear();
					useRoleNotActiveStore.getState().clear();
					set({ user: null, otpSent: false, pendingPhone: null });
				}
			},

			/**
			 * Set user type during onboarding
			 */
			setUserType: async (userType: UserType) => {
				set({ isLoading: true, error: null });
				try {
					const response = await authService.setUserType(userType);
					analytics.onboarding.userTypeSelected({ userType });
					analytics.updateUserTraits({ userType });
					clearCacheOnAccountChange(get().user, response.data.user);
					set({ user: response.data.user, isLoading: false });
				} catch (error) {
					const err = error as { response?: { data?: { message?: string } } };
					const errorMessage =
						err.response?.data?.message || "Failed to set user type";
					set({ error: errorMessage, isLoading: false });
					throw error;
				}
			},

			switchRole: async (role: UserType) => {
				set({ error: null });
				ownRoleChangesInFlight += 1;
				let failed = false;
				try {
					const user = await authService.switchActiveRole(role);
					adoptActiveRole(user);
					return user;
				} catch (error) {
					failed = true;
					const err = error as { response?: { data?: { message?: string } } };
					set({
						error: err.response?.data?.message || "Failed to switch role",
					});
					throw error;
				} finally {
					ownRoleChangesInFlight -= 1;
					if (failed) void reconcileActiveRole();
				}
			},

			addRole: async (role: UserType) => {
				set({ error: null });
				ownRoleChangesInFlight += 1;
				let failed = false;
				try {
					const user = await authService.addRole(role);
					adoptActiveRole(user);
					return user;
				} catch (error) {
					failed = true;
					const err = error as { response?: { data?: { message?: string } } };
					set({
						error: err.response?.data?.message || "Failed to add role",
					});
					throw error;
				} finally {
					ownRoleChangesInFlight -= 1;
					if (failed) void reconcileActiveRole();
				}
			},

			/**
			 * Update language preference
			 */
			setLanguagePreference: async (languagePreference: "en" | "hi") => {
				set({ isLoading: true, error: null });
				try {
					const response =
						await authService.updateLanguagePreference(languagePreference);
					clearCacheOnAccountChange(get().user, response.data.user);
					set({ user: response.data.user, isLoading: false });
				} catch (error) {
					const err = error as { response?: { data?: { message?: string } } };
					const errorMessage =
						err.response?.data?.message || "Failed to update language";
					set({ error: errorMessage, isLoading: false });
					throw error;
				}
			},

			/**
			 * Manually set user
			 */
			setUser: (user) => {
				clearCacheOnAccountChange(get().user, user);
				set({ user });
			},

			/**
			 * Attach a phone to the current user after lazy collection.
			 */
			setPhone: (phone) => {
				const user = get().user;
				if (user) set({ user: { ...user, phone } });
			},

			/**
			 * Mark the current user's profile as completed.
			 * Called after successful onboarding to prevent routing
			 * back to the onboarding flow.
			 */
			markProfileComplete: () => {
				const user = get().user;
				if (user) {
					set({ user: { ...user, profileCompleted: true } });
				}
			},

			/**
			 * Re-fetch the current user from the backend to sync
			 * profileCompleted and other stale fields from localStorage.
			 */
			refreshUser: async () => {
				const user = get().user;
				if (!user) return;
				try {
					const refreshed = await authService.refreshCurrentUser();
					// If anything replaced the user while the request was out --
					// another tab switching accounts, a sign-in, a finished profile,
					// or an overlapping refresh that answered first -- keep that and
					// drop this answer.
					if (get().user !== user) return;
					if (refreshed) {
						clearCacheOnAccountChange(user, refreshed);
						set({ user: refreshed });
						if (isRoleSwitch(user, refreshed)) {
							showRoleSwitchedElsewhereNotice(refreshed.userType);
						}
					}
				} catch {
					// Silently fail — stale state is better than blocking the app
				}
			},

			/**
			 * Clear error state
			 */
			clearError: () => set({ error: null }),

			/**
			 * Reset OTP state (for going back)
			 */
			resetOtpState: () => set({ otpSent: false, pendingPhone: null }),

			/**
			 * Check if user is authenticated
			 */
			isAuthenticated: () => !!get().user,

			/**
			 * Check if user needs onboarding (no user type set)
			 */
			needsOnboarding: () => {
				const user = get().user;
				return !!user && !user.userType;
			},

			/**
			 * Check if user has selected a type but hasn't completed profile setup
			 */
			needsProfileSetup: () => {
				const user = get().user;
				return !!user && !!user.userType && !user.profileCompleted;
			},

			/**
			 * Check if the user has no phone yet (Google account, lazy collection)
			 */
			needsPhoneCollection: () => {
				const user = get().user;
				return !!user && user.phone === null;
			},

			/**
			 * Get current user type
			 */
			getUserType: () => get().user?.userType || null,
		}),
		{
			name: AUTH_STORE_KEY,
			version: 1,
			storage: createJSONStorage(() => localStorage),
			partialize: (state) => ({
				user: state.user,
			}),
			migrate: migrateAuthState,
		},
	),
);

/**
 * Adopt a user this tab's own switch or add-role returned, then clear the
 * previous role's data. The other tabs follow through the storage write.
 */
function adoptActiveRole(user: FinishdUser): void {
	useAuthStore.setState({ user });
	resetRoleScopedState();
	if (user.userType) {
		analytics.updateUserTraits({ userType: user.userType });
	}
}

/**
 * Follow a sign-in, sign-out or role switch made in another tab. The session
 * cookies are shared across tabs but this store's memory is not: after
 * another tab signs in as a different account, this tab would otherwise keep
 * showing the first account while its requests run as the second.
 *
 * A `storage` event fires only in the tabs that did not make the write, and
 * only when the stored value actually changed.
 */
export function handleAuthStorageEvent(event: StorageEvent): void {
	if (event.key !== AUTH_STORE_KEY && event.key !== null) return;

	let next: FinishdUser | null = null;
	if (event.newValue) {
		try {
			const stored = JSON.parse(event.newValue) as {
				state?: unknown;
				version?: number;
			};
			next = migrateAuthState(stored.state, stored.version ?? 0).user;
		} catch {
			// Not a value this store wrote; nothing to follow.
			return;
		}
	}

	// Only a change of account or of active role is followed. Adopting any
	// other same-account write would make this tab persist it back, and two
	// tabs holding different bytes for one account would then hand it back
	// and forth. A followed value is the parsed `newValue` itself, so this
	// tab's persist writes back exactly those bytes, which fires no event.
	const previous = useAuthStore.getState().user;
	if (previous?.id === next?.id && !isRoleSwitch(previous, next)) return;

	clearCacheOnAccountChange(previous, next);
	useAuthStore.setState({ user: next });
	if (isRoleSwitch(previous, next)) {
		showRoleSwitchedElsewhereNotice(next.userType);
	}
}

let followingActiveRole: Promise<void> | null = null;

/**
 * Refresh the user to learn the account's active role, with at most one such
 * refresh out at a time: a call made while one is out returns that one.
 */
function reconcileActiveRole(): Promise<void> {
	if (!followingActiveRole) {
		followingActiveRole = useAuthStore
			.getState()
			.refreshUser()
			.finally(() => {
				followingActiveRole = null;
			});
	}
	return followingActiveRole;
}

/**
 * Follow a role switch made on another device. Every authenticated response
 * names the account's active role; one that differs from the stored user's
 * refreshes the user. When the refreshed user has a different role,
 * `refreshUser` clears the old role's data and shows the notice. While that
 * refresh is out, further mismatching responses (including the refresh's own)
 * start no other. While this tab's own switch or add-role is awaiting the
 * server, the header is ignored: that call adopts the new role itself when
 * it succeeds, and refreshes the user when it fails.
 */
export function handleActiveRoleHeader(role: string): void {
	const user = useAuthStore.getState().user;
	if (!user || !isUserType(role) || role === user.userType) return;
	if (followingActiveRole || ownRoleChangesInFlight > 0) return;
	void reconcileActiveRole();
}

/**
 * A role-gated request was refused because `role` is held but not active.
 * Refresh the user, in case the stored active role is stale, then record
 * `role` for the switch prompt. Recording it after the refresh settles keeps
 * it: a refresh that finds a different active role clears the role stores,
 * the prompt's included. Nothing is recorded when the stored account was
 * signed out or replaced while the refresh was out.
 */
export function handleRoleNotActive(role: string): void {
	const userId = useAuthStore.getState().user?.id;
	if (!userId || !isUserType(role)) return;
	void reconcileActiveRole().finally(() => {
		if (useAuthStore.getState().user?.id !== userId) return;
		useRoleNotActiveStore.getState().raise(role);
	});
}

/**
 * The server refused to refresh the session, for example because it expired
 * or was revoked. The shared client has already cleared the stored tokens.
 * Drop the signed-in user and all role data here. This does not
 * navigate: `ProtectedRoute` sends a visitor on a protected route to /login,
 * and a public page stays open.
 */
export function handleSessionLost(): void {
	resetRoleScopedState();
	useAuthStore.setState({ user: null, otpSent: false, pendingPhone: null });
}

if (typeof window !== "undefined") {
	window.addEventListener("storage", handleAuthStorageEvent);
}

observeRoleResponses({
	activeRole: handleActiveRoleHeader,
	roleNotActive: handleRoleNotActive,
});

observeSessionLoss(handleSessionLost);
